/* Tiloli Home promo carousel — #17. Client-side narrowing + top-N + scroller over the REAL,
   server-rendered product cards (snippets/tiloli-card.liquid, via sections/promo-carousel.liquid —
   one <li class="product-grid__item" data-pct data-brand data-devices data-l1 data-l2 data-l3
   data-price> per product, wrapping the real `.tlc` card markup). This script does NOT build
   cards — it only reads the `data-*` attributes on each server-rendered `<li>` inside
   #promo-carousel-track to keep the ones with data-pct > 0 for THIS viewer, sort by data-pct
   desc, reveal the top N (data-max-items, default 12), remove the rest, and wire prev/next to
   scroll the track. Mirrors assets/tiloli-promo-hub.js's read-data-*-off-the-DOM approach.

   Price hydration: the card's live B2B net price is hydrated by assets/tiloli-card.js, which on
   DOMContentLoaded does `document.querySelectorAll('.tl-price[data-cust]:not(.tl-price--ready)')`
   over the WHOLE document and batches one fetch for every matched variant, plus a MutationObserver
   that catches any later-added node. Because this section server-renders every candidate card into
   the DOM at initial page load (nothing is client-built here), that first querySelectorAll pass
   already finds every `.tl-price` in the carousel — including the ones this script removes for
   being outside the top N. Removing the excluded nodes from the DOM does not affect the reveal
   already applied (or in flight) for the ones that stay; no extra wiring is needed here.

   No framework, no deps. Classic deferred <script> — no ES module import/export. */

if (!window.__tlPromoCarouselInit) {
  window.__tlPromoCarouselInit = true;

  const SECTION_SEL = '.promo-carousel';
  const TRACK_SEL = '#promo-carousel-track';
  const PREV_SEL = '#promo-carousel-prev';
  const NEXT_SEL = '#promo-carousel-next';

  // ---- Reading the server-rendered <li> nodes ---------------------------------------------------
  // Each <li class="product-grid__item"> was emitted by sections/promo-carousel.liquid wrapping
  // the REAL `{% render 'tiloli-card' %}` markup — this script never creates or mutates that
  // markup, it only reads data-pct to filter/sort and reorders/removes the <li> nodes themselves.
  function parseListItem(li) {
    return { el: li, pct: Number(li.dataset.pct) || 0, date: Number(li.dataset.date) || 0 };
  }

  // ---- Main ------------------------------------------------------------------------------------
  function init() {
    const section = document.querySelector(SECTION_SEL);
    if (!section) return;

    const track = section.querySelector(TRACK_SEL) || document.querySelector(TRACK_SEL);
    if (!track) {
      section.hidden = true;
      return;
    }

    const maxItems = Number(section.dataset.maxItems) || 12;

    // Snapshot the server-rendered <li> nodes ONCE and compute their sort key from data-pct.
    const items = Array.from(track.querySelectorAll(':scope > li')).map(parseListItem);

    // Keep only what THIS viewer has a live promo on (data-pct > 0, computed server-side exactly
    // like the hub), sort by pct desc, take the top N. Nodes outside the top N are removed from
    // the DOM (not just hidden) — the carousel is a fixed-size teaser, not a paginated browse.
    const eligible = items.filter((it) => it.pct > 0).sort((a, b) => b.date - a.date);
    const shown = eligible.slice(0, maxItems);
    const shownSet = new Set(shown.map((it) => it.el));

    items.forEach((it) => {
      if (!shownSet.has(it.el)) it.el.remove();
    });

    // Zero promos for this viewer (anonymous with no public promos, or a reseller with none of
    // their own): hide the whole section so no bare heading / empty scroller ever shows.
    if (shown.length === 0) {
      section.hidden = true;
      return;
    }

    // Reorder the kept <li> nodes in the DOM to match the sort (data-pct desc) — moving the real
    // server-rendered nodes, not rebuilding them, so the `.tlc` markup/price/state inside each is
    // never touched.
    const frag = document.createDocumentFragment();
    shown.forEach((it) => frag.appendChild(it.el));
    track.appendChild(frag);

    section.hidden = false;

    // ---- Nav (.tlcn) : barre de progression + flèches (desktop) + pastilles (mobile) ----------
    // Même comportement que tiloli-carousel-nav.js, mais piloté ici (un seul contrôleur, pas de
    // custom-element qui s'auto-upgraderait via son propre JS chargé ailleurs sur la page).
    const prevBtn = section.querySelector(PREV_SEL);
    const nextBtn = section.querySelector(NEXT_SEL);
    const thumb = section.querySelector('[data-pc-thumb]');
    const dotsWrap = section.querySelector('[data-pc-dots]');

    const step = () => {
      const slide = track.querySelector(':scope > li');
      const gap = parseFloat(getComputedStyle(track).columnGap || '16') || 16;
      return slide ? slide.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };

    if (prevBtn) prevBtn.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (nextBtn) nextBtn.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

    // pastilles mobile — une par slide visible (après filtrage/réordonnancement)
    const dots = [];
    const slides = Array.from(track.querySelectorAll(':scope > li'));
    if (dotsWrap && slides.length > 1) {
      slides.forEach((s, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'tlcn__dot';
        d.setAttribute('aria-label', 'Slide ' + (i + 1));
        d.addEventListener('click', () => track.scrollTo({ left: i * step(), behavior: 'smooth' }));
        dotsWrap.appendChild(d);
        dots.push(d);
      });
    }

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const ratio = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
      const w = Math.max(12, Math.min(100, ratio * 100));
      const pos = max > 0 ? track.scrollLeft / max : 0;
      if (thumb) {
        thumb.style.width = w + '%';
        thumb.style.left = (pos * (100 - w)) + '%';
      }
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 2;
      if (dots.length) {
        const s = step();
        const idx = Math.max(0, Math.min(dots.length - 1, s > 0 ? Math.round(track.scrollLeft / s) : 0));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
    };

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    // scrollWidth sous-évalué au 1er paint (images lazy) → recalculs différés
    requestAnimationFrame(update);
    [120, 400, 1000].forEach((t) => setTimeout(update, t));
    if ('ResizeObserver' in window) { new ResizeObserver(update).observe(track); }
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
